import React from 'react';

// Reusable Gauge Component
const ScoreGauge = ({ score = 626, min = 300, max = 900 }) => {
  const percent = Math.min(Math.max((score - min) / (max - min), 0), 1);
  
  // SVG Arc calculations
  const radius = 70;
  const strokeWidth = 16;
  const arcLength = Math.PI * radius; // Half circle
  const filledLength = arcLength * percent;
  
  // Arrow position calculations (using radians)
  // Angle goes from PI (left/300) to 2*PI (right/900)
  const angle = Math.PI + (percent * Math.PI);
  
  // Tip of the arrow (sits just inside the inner edge of the stroke)
  const innerRadius = radius - strokeWidth / 2 - 2; 
  const tipX = 100 + innerRadius * Math.cos(angle);
  const tipY = 100 + innerRadius * Math.sin(angle);
  
  // Base of the arrow triangle (closer to center)
  const baseRadius = innerRadius - 10;
  const baseX = 100 + baseRadius * Math.cos(angle);
  const baseY = 100 + baseRadius * Math.sin(angle);
  
  // Perpendicular points for the triangle base
  const perpAngle = angle + Math.PI / 2;
  const halfWidth = 5;
  const p1X = baseX + halfWidth * Math.cos(perpAngle);
  const p1Y = baseY + halfWidth * Math.sin(perpAngle);
  const p2X = baseX - halfWidth * Math.cos(perpAngle);
  const p2Y = baseY - halfWidth * Math.sin(perpAngle);
  
  const arrowPoints = `${tipX},${tipY} ${p1X},${p1Y} ${p2X},${p2Y}`;

  return (
    <div className="relative flex flex-col items-center justify-center w-48 h-32">
      <svg width="200" height="120" viewBox="0 0 200 120" className="overflow-visible">
        {/* Gray Background Arc */}
        <path
          d={`M ${100 - radius} 100 A ${radius} ${radius} 0 0 1 ${100 + radius} 100`}
          fill="none"
          stroke="#e5e7eb" // Tailwind gray-200
          strokeWidth={strokeWidth}
        />
        
        {/* Cyan Foreground Arc (Score) */}
        <path
          d={`M ${100 - radius} 100 A ${radius} ${radius} 0 0 1 ${100 + radius} 100`}
          fill="none"
          stroke="#00a1de" // CIBIL Cyan
          strokeWidth={strokeWidth}
          strokeDasharray={arcLength}
          strokeDashoffset={arcLength - filledLength}
        />

        {/* Black Pointer Arrow */}
        <polygon points={arrowPoints} fill="#000000" />
      </svg>
      
      {/* Score Text */}
      <div className="absolute top-[55px] flex flex-col items-center">
        <span className="text-4xl font-extrabold text-black custom-font tracking-tighter">
          {score}
        </span>
      </div>
      
      {/* Min/Max Labels */}
      <div className="absolute bottom-1 w-full flex justify-between px-6">
        <span className="text-gray-400 text-xs font-bold custom-font">{min}</span>
        <span className="text-gray-400 text-xs font-bold custom-font">{max}</span>
      </div>
    </div>
  );
};

// Reusable Data Row Component to keep the grid aligned
const InfoRow = ({ label, value }) => (
  <div className="flex mb-3 text-[11px] tracking-wide custom-font">
    <div className="w-[140px] text-[#00a1de] font-bold uppercase shrink-0">{label}</div>
    <div className="w-4 text-black font-bold shrink-0">:</div>
    <div className="flex-1 text-black font-bold">{value}</div>
  </div>
);

export default function ConsumerCIR({reportData}) {

  const formatDate = (dateString) => {
  if (!dateString) return "-";
  
 
  const [year, month, day] = dateString.split('+')[0].split('-');
  
  // Return in DD/MM/YYYY format for the CIBIL UI
  return `${day}/${month}/${year}`; 
};

  const asset = reportData?.asset
  return (
    <div className="w-full max-w-5xl p-6 bg-white custom-font">
      
      {/* HEADER SECTION */}
      <h2 className="text-[22px] font-bold text-gray-800 mb-4 tracking-wide uppercase">
        CONSUMER CIR
      </h2>

      {/* Member ID Block */}
      <div className="border border-gray-400 rounded-lg p-4 mb-8 flex justify-between items-center">
        <div className="flex text-xs tracking-wide">
          <span className="text-[#00a1de] font-bold uppercase w-28">MEMBER ID</span>
          <span className="text-black font-bold mx-2">:</span>
          <span className="text-black font-bold">N/A</span>
        </div>
        <div className="flex text-xs tracking-wide pr-32">
          <span className="text-[#00a1de] font-bold uppercase w-36">REFERENCE KEY</span>
          <span className="text-black font-bold mx-2">:</span>
          <span className="text-black font-bold">{asset?.TrueLinkCreditReport?.ReferenceKey || "N/A"} </span>
        </div>
      </div>

      {/* CONSUMER INFORMATION SECTION */}
      <h3 className="text-lg font-bold text-gray-800 mb-2 uppercase">
        CONSUMER INFORMATION
      </h3>
      <div className="border border-gray-400 rounded-lg p-5 mb-8">
        <div className="grid grid-cols-2 gap-x-8">
          {/* Column 1 */}
          <div>
            <InfoRow label="CONSUMER NAME" value={reportData?.personal?.name || "N/A"} />
            <InfoRow label="DOB" value={formatDate(reportData?.personal?.dob) || "N/A"} />
            <InfoRow label="TELEPHONE NO." value={reportData?.personal?.phone || "N/A"}/>
            <InfoRow label="EMAIL ID" value={reportData?.personal?.email?.toLowerCase() || "N/A"} />
            <InfoRow label="GENDER" value={reportData?.personal?.gender || "N/A"} />
            <InfoRow label="ADDRESS" value={reportData?.personal?.address || "N/A"} />
          </div>
          {/* Column 2 */}
          <div>
            <InfoRow label="PAN" value={reportData?.personal?.pan || "N/A"} />
            <InfoRow label="DRIVING LICENCE NO" value="-" />
            <InfoRow label="VOTER ID" value="-" />
            <InfoRow label="PASSPORT NO." value="-" />
            <InfoRow label="AADHAAR NUMBER (UID)" value="NOT DISCLOSED" />
          </div>
        </div>
      </div>

      {/* CIBIL TRANSUNION SCORE(S) SECTION */}
      <h3 className="text-lg font-bold text-gray-800 mb-2 uppercase">
        CIBIL TRANSUNION SCORE(S)
      </h3>
      <div className="border border-gray-400 rounded-lg p-6 flex justify-between items-center ">
        
        {/* Left: Score Meta */}
        <div className="flex flex-col w-1/4 pt-6">
          <div className="text-xl text-black font-extrabold uppercase mb-1">
            CREDITVISION<sup className="text-sm font-medium">®</sup>
          </div>
          <div className="text-xl text-black font-bold mb-3">Score</div>
          <div className="text-[#00a1de] text-[10px] font-bold uppercase">Ranges from:</div>
          <div className="text-[#00a1de] text-[10px] font-bold">300 (high risk) to 900 (low risk)</div>
        </div>

        {/* Center: Gauge Chart */}
        <div className="w-2/4 flex justify-center items-end h-full pb-4">
          <ScoreGauge score={reportData?.score?.value || "N/A"} />
        </div>

        {/* Right: Scoring Factors */}
        <div className="w-1/4 h-full pt-4">
          <h4 className="text-[#00a1de] text-xs font-bold uppercase mb-2">SCORING FACTORS</h4>
          <ul className="text-black text-[11px] font-bold leading-relaxed list-none">
            <li>1. Presence Of Delinquency</li>
            <li>2. Presence Of Delinquency In The Recent Past</li>
            <li>3. Presence Of Severe Delinquency As Of Recent Update</li>
            <li>4. High Balance Build-Up On Secured Loans</li>
            <li>5. Low Credit Age</li>
          </ul>
        </div>

      </div>
    </div>
  );
}